import { useState, useEffect, useCallback, useRef } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { SAMPLE_PAGES } from "@/lib/books";

const variants = {
  enter: (dir) => ({ rotateY: dir > 0 ? 70 : -70, opacity: 0, transformOrigin: dir > 0 ? "left center" : "right center" }),
  center: { rotateY: 0, opacity: 1 },
  exit: (dir) => ({ rotateY: dir > 0 ? -70 : 70, opacity: 0, transformOrigin: dir > 0 ? "right center" : "left center" }),
};

const NavBtn = ({ onClick, disabled, label, testid, children }) => (
  <button onClick={onClick} disabled={disabled} aria-label={label}
    className="w-12 h-12 shrink-0 rounded-full flex items-center justify-center border bg-white transition-opacity disabled:opacity-30"
    style={{ borderColor: "rgba(11,27,61,0.15)", color: "var(--navy)" }} data-testid={testid}>{children}</button>
);

export const PageFlip = () => {
  const [[i, dir], setPage] = useState([0, 0]);
  const dragged = useRef(false);
  const last = SAMPLE_PAGES.length - 1;
  const go = useCallback((d) => setPage(([p]) => [Math.min(last, Math.max(0, p + d)), d]), [last]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.target.closest?.("input, textarea, select, [contenteditable]")) return;
      if (e.key === "ArrowRight") go(1);
      if (e.key === "ArrowLeft") go(-1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [go]);

  const page = SAMPLE_PAGES[i];
  return (
    <div data-testid="book-page-flip">
      <div className="relative aspect-[4/3] rounded-3xl overflow-hidden" style={{ backgroundColor: "#F3EEE3", perspective: 1600 }}>
        <AnimatePresence initial={false} custom={dir} mode="wait">
          <motion.img
            key={page.src} src={page.src} alt={page.caption} custom={dir} variants={variants}
            initial="enter" animate="center" exit="exit" transition={{ duration: 0.45, ease: [0.4, 0, 0.2, 1] }}
            drag="x" dragConstraints={{ left: 0, right: 0 }} dragElastic={0.3}
            onDragStart={() => { dragged.current = true; }}
            onDragEnd={(_, info) => { if (info.offset.x < -60) go(1); else if (info.offset.x > 60) go(-1); setTimeout(() => { dragged.current = false; }, 50); }}
            onTap={() => { if (!dragged.current) go(i === last ? -last : 1); }}
            className={`absolute inset-0 m-auto max-w-full max-h-full cursor-pointer select-none ${page.cover ? "h-[92%] w-auto rounded-lg" : "w-[96%] h-auto rounded-xl"}`}
            style={{ boxShadow: "0 20px 40px rgba(11,27,61,0.2)" }}
            draggable={false} data-testid="book-page-image"
          />
        </AnimatePresence>
      </div>
      <div className="flex items-center justify-between gap-4 mt-6">
        <NavBtn onClick={() => go(-1)} disabled={i === 0} label="Previous page" testid="book-page-prev"><ChevronLeft size={20} /></NavBtn>
        <div className="text-center min-w-0">
          <p className="font-display text-lg sm:text-xl leading-snug line-clamp-2" style={{ color: "var(--navy)" }} data-testid="book-page-caption">{page.caption}</p>
          <p className="font-body text-xs mt-1" style={{ color: "var(--muted)" }} data-testid="book-page-counter">{i + 1} of {SAMPLE_PAGES.length} · tap or swipe to turn</p>
        </div>
        <NavBtn onClick={() => go(1)} disabled={i === last} label="Next page" testid="book-page-next"><ChevronRight size={20} /></NavBtn>
      </div>
      <div className="flex justify-center gap-2 mt-5">
        {SAMPLE_PAGES.map((p, n) => (
          <button key={p.src} onClick={() => setPage([n, n > i ? 1 : -1])} aria-label={`Go to page ${n + 1}`}
            className="h-2 rounded-full transition-all" style={{ width: n === i ? 24 : 8, backgroundColor: n === i ? "var(--gold)" : "rgba(11,27,61,0.18)" }}
            data-testid={`book-page-dot-${n}`} />
        ))}
      </div>
    </div>
  );
};
